import { Button, CloseButton, Divider, Grid, Group, Modal, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { freightCarrierDetailsModalMessages } from './freight-carrier-details-modal-messages';
import styles from './FreightCarrierDetailsModal.module.css';
import { getCarrierInitials } from '../get-carrier-initials';
import { getCarrierLogoSrc } from '../get-carrier-logo-src';

import type { FreightCarrierDetailsModalProps } from './freight-carrier-details-modal-types';
import type { LtlAddress } from '../../../types/ltl';
import type { ShipFromAddress } from '../../../types/ship-from';

type TerminalColumnProps = {
  label: string;
  code?: string | null;
  address: ShipFromAddress | LtlAddress;
};

const TerminalColumn = ({ label, code, address }: TerminalColumnProps) => (
  <Grid.Col span={1}>
    <Stack gap={4}>
      <Text className={styles.terminalLabel}>{label}</Text>
      {code && <Text className={styles.terminalCode}>{code}</Text>}
      {(address.line1 || address.line2) && (
        <Text className={styles.terminalAddress}>
          {address.line1}
          {address.line2 ? `, ${address.line2}` : ''}
        </Text>
      )}
      <Text className={styles.terminalAddress}>
        {address.city}, {address.state} {address.postalCode}
      </Text>
      {address.phone && <Text className={styles.terminalAddress}>{address.phone}</Text>}
    </Stack>
  </Grid.Col>
);

export const FreightCarrierDetailsModal = ({
  opened,
  onClose,
  carrierName,
  carrierScac,
  quote,
  shipFromAddress,
  destinationAddress,
  liabilityLimits,
  zIndex,
}: FreightCarrierDetailsModalProps) => {
  const intl = useIntl();
  const logoSrc = getCarrierLogoSrc(carrierScac);

  const formatAmount = (value?: number) => (value != null ? intl.formatNumber(value, { style: 'currency', currency: 'USD' }) : '—');

  return (
    <Modal
      centered
      classNames={{ body: styles.modalBody, content: styles.modalContent }}
      onClose={onClose}
      opened={opened}
      size='md'
      withCloseButton={false}
      zIndex={zIndex}
    >
      <Stack gap='md' p='md'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.title)}</Text>
          <CloseButton onClick={onClose} />
        </Group>

        <Group gap='sm'>
          <div className={styles.logoBox}>
            {logoSrc ? (
              <img alt={carrierName} className={styles.logoImage} src={logoSrc} />
            ) : (
              <span className={styles.logoInitials}>{getCarrierInitials(carrierName)}</span>
            )}
          </div>
          <Text fw={600}>{carrierName}</Text>
        </Group>

        <Divider />

        <Stack gap='xs'>
          <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.terminalInformation)}</Text>
          <Grid columns={2} gutter='md'>
            <TerminalColumn
              address={shipFromAddress}
              code={quote.originTerminalCode}
              label={intl.formatMessage(freightCarrierDetailsModalMessages.pickupLabel)}
            />
            <TerminalColumn
              address={destinationAddress}
              code={quote.destinationTerminalCode}
              label={intl.formatMessage(freightCarrierDetailsModalMessages.deliveryLabel)}
            />
          </Grid>
        </Stack>

        <Divider />

        <Stack gap='xs'>
          <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.transitInformation)}</Text>
          <Text>{intl.formatMessage(freightCarrierDetailsModalMessages.estimatedTransit, { days: quote.transitDays })}</Text>
        </Stack>

        {liabilityLimits && (
          <>
            <Divider />
            <Stack gap='xs'>
              <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.liabilityLimits)}</Text>
              <Text>
                {intl.formatMessage(freightCarrierDetailsModalMessages.liabilityNew, {
                  amount: formatAmount(liabilityLimits.newValue),
                })}
              </Text>
              <Text>
                {intl.formatMessage(freightCarrierDetailsModalMessages.liabilityUsed, {
                  amount: formatAmount(liabilityLimits.usedValue),
                })}
              </Text>
            </Stack>
          </>
        )}

        <Group justify='flex-end'>
          <Button onClick={onClose} variant='tertiary'>
            {intl.formatMessage(freightCarrierDetailsModalMessages.close)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
